import { ContactService } from '@amzn/awscat-portfolio-assessment-service-client';
import { Contact, UpdateContactParams } from '@amzn/awscat-portfolio-assessment-types';
import { FlashContextInterface, FlashType, withFlashContext } from '@amzn/awscat-react-components';
import { Box, Button, FormField, Input, Modal, SpaceBetween } from '@amzn/awsui-components-react';
import { useState } from 'react';
import { match, useRouteMatch } from 'react-router';

import { constants } from '../../../common/Localization';
import { MatchParam } from '../../../common/types';
import { validateEmail, validatePhone } from '../../portfolios/util';
import { MAX_NAME_CHARACTERS } from './constants';
import { ContactActions } from './contact-management-helper';

type Props = FlashContextInterface & {
    action: ContactActions;
    contact: Contact | null;
    contacts: Contact[];
    visible: boolean;
    setVisible: (visible: boolean) => void;
    onConfirmChange: () => void;
}

const contactService = new ContactService();

function ContactActionModal({ action, contact, contacts, visible, setVisible, onConfirmChange, flash }: Props): JSX.Element {
    const match: match<MatchParam> | null = useRouteMatch({
        path: '/portfolio/:portfolioId',
    });
    const portfolioId = match?.params?.portfolioId;
    
    const [ name, setName ] = useState<string>(action === 'add' ? '' : (contact?.name ?? ''));
    const [ email, setEmail ] = useState<string>(action === 'add' ? '' : (contact?.email ?? ''));
    const [ phone, setPhone ] = useState<string>(action === 'add' ? '' : (contact?.phone ?? ''));
    const [ loading, setLoading ] = useState(false);
    
    const dismissModal = (): void => setVisible(false);

    const getHeader = () => {
        switch(action) {
            case 'add':
                return constants.ADD_NEW_CONTACT;
            case 'edit':
                return constants.EDIT_CONTACT;
        }
    };

    const changeContact = async () => {
        if (!portfolioId) {
            return;
        }

        const data: UpdateContactParams = {
            contactId: contact?.contactId ?? '',
            name,
            email,
            phone,
            portfolioId,
        };

        setLoading(true);
        try {
            switch(action){
                case 'add':
                    await contactService.create(portfolioId, data);
                    break;
                case 'edit':
                    await contactService.update(portfolioId, data);
                    break;
            }
        } catch (err) {
            flash.alert(FlashType.error, (err as Error).message, '');
        } finally {
            setLoading(false);
            dismissModal();
            onConfirmChange();
        }
    };
    
    const isInvalidForm = () => {
        return validateEmailAndCheckForDuplicate(email).length > 0 
            || (phone.length > 0 && validatePhone(phone).length > 0)
            || isInvalidName();
    };

    const isInvalidName = () => {
        return name.length === 0 || name.length > MAX_NAME_CHARACTERS;
    };

    const validateName = (): string => {
        if (name.length > MAX_NAME_CHARACTERS) {
            return `${constants.NAME_CANNOT_EXCEED_MSG} ${MAX_NAME_CHARACTERS} ${constants.CHARACTERS}`;
        } else {
            return '';
        }
    };

    const validateEmailAndCheckForDuplicate = (email: string): string => {
        if (email !== contact?.email && // On edit, ignore existing email value
            contacts.some(x => x?.email?.toLowerCase() === email.toLowerCase())){
            return constants.EMAIL_IN_USE;
        } else {
            return validateEmail(email);
        }
    };

    const addEditContent = (
        <SpaceBetween size='m'>
            <FormField
                data-testid='nameInput'
                label={`${constants.NAME} *`}
                errorText={validateName()}
            >
                <Input
                    value={name}
                    onChange={({ detail }) => setName(detail.value)}
                />
            </FormField>
            <FormField
                data-testid='emailInput'
                label={`${constants.EMAIL} *`}
                errorText={validateEmailAndCheckForDuplicate(email)}
            >
                <Input
                    value={email}
                    onChange={({ detail }) => setEmail(detail.value)}
                    inputMode='email'
                />
            </FormField>
            <FormField
                label={constants.PHONE}
                errorText={phone.length > 0 ? validatePhone(phone) : ''}
            >
                <Input
                    value={phone}
                    onChange={({ detail }) => setPhone(detail.value)}
                />
            </FormField>
        </SpaceBetween>
    );

    const getFooter = () => {
        return (
            <Box float='right' data-testid='contactActionFooter'>
                <SpaceBetween size='s' direction='horizontal'>
                    <Button onClick={dismissModal}>{constants.CANCEL_BUTTON_TEXT}</Button>
                    <Button 
                        data-testid='contactActionSubmit'
                        variant='primary'
                        loading={loading}
                        onClick={changeContact}
                        disabled={isInvalidForm()}
                    >
                        {constants.SAVE_BUTTON_TEXT}
                    </Button>
                </SpaceBetween>
            </Box>
        );
    };

    return(
        <Modal
            data-testid='contactAction'
            header={getHeader()}
            footer={getFooter()}
            visible={visible}
            onDismiss={dismissModal}
        >
            {addEditContent}
        </Modal>
    );
}

export default withFlashContext(ContactActionModal);
